/**
 * Centralised human-readable labels for every field key used across all program types
 * (AI Led, AI Led Nested, Trainer Led, Objective, Subjective).
 *
 * Used in change-diff summaries sent in update notification emails.
 * Any key not listed here falls back to the raw key name.
 */

// ── Program-level fields (all types) ────────────────────────────────────────
const PROGRAM_FIELDS: Record<string, string> = {
    trainingName:          'Program Name',
    fullname:              'Program Name',
    trainingId:            'Training ID',
    trainingid:            'Training ID',
    trainingType:          'Training Type',
    trainingtype:          'Training Type',
    passingPercentage:     'Passing Percentage',
    passingpercentage:     'Passing Percentage',
    estimatedEffortHours:  'Estimated Effort',
    estimatedefforthours:  'Estimated Effort',
    startDate:             'Start Date',
    startdate:             'Start Date',
    endDate:               'End Date',
    enddate:               'End Date',
    trainingLink:          'Resource Link',
    traininglink:          'Resource Link',
    audienceEmails:        'Participants',
    audienceemails:        'Participants',
    stakeholderEmails:     'Stakeholders',
    stakeholderemails:     'Stakeholders',
    trainerEmails:         'Trainers',
    traineremails:         'Trainers',
    programType:           'Program Type',
    programtype:           'Program Type',
    visible:               'Visibility',
    status:                'Status',
    createdBy:             'Created By',
    createdby:             'Created By',
    notificationVersion:   'Notification Version',
    assessments:           'Assessment Structure',
    ispublished:           'Published',
};

// ── AI Led / AI Led Nested — module-level fields ─────────────────────────────
const AI_MODULE_FIELDS: Record<string, string> = {
    name:           'Module Name',
    description:    'Description',
    estimatedTime:  'Estimated Time',
    attemptLimit:   'Attempt Limit',
    deadline:       'Deadline',
    questions:      'Questions',
    codeSnippet:    'Code Snippet',
    answer:         'Reference Answer',
    question:       'Question Text',
    level:          'Difficulty Level',
    quantity:       'Question Count',
};

// ── Trainer Led — assignment-level fields ────────────────────────────────────
const TRAINER_LED_FIELDS: Record<string, string> = {
    title:           'Title',
    content:         'Instructions',
    description:     'Instructions',
    deadline:        'Proposal Deadline',
    maxCredits:      'Max Credits',
    achievedCredits: 'Achieved Credits',
    grade:           'Max Credits',
    workflowEnabled: 'Workflow Enabled',
    stateDeadlines:  'Submission Deadline',
    introfiles:      'Reference Files',
    submissionfiles: 'Submission Files',
    moodleId:        'Module ID',
    sectionname:     'Section',
    sectionnum:      'Section Number',
    workflowenabled: 'Workflow Enabled',
    statedeadlines:  'Submission Deadline',
};

// ── Objective / Subjective — question-level fields ───────────────────────────
const OBJECTIVE_FIELDS: Record<string, string> = {
    module:        'Module',
    question:      'Question',
    answer:        'Answer',
    options:       'Options',
    correctOption: 'Correct Option',
    explanation:   'Explanation',
    marks:         'Marks',
    difficulty:    'Difficulty',
    tags:          'Tags',
};

/**
 * Merged label map covering all program types.
 * Look up any field key to get its display name.
 */
export const FIELD_LABELS: Record<string, string> = {
    ...PROGRAM_FIELDS,
    ...AI_MODULE_FIELDS,
    ...TRAINER_LED_FIELDS,
    ...OBJECTIVE_FIELDS,
};

/**
 * Fields that must never be compared — client-side identifiers that
 * change every page load and would produce false positives.
 */
export const SKIP_FIELDS = new Set<string>(['id']);
